import type { ZoneTemplate } from './types';

// Template 1 — Classic Gym Board. Player-stat panels flanking a center
// score/clock stack; sponsor strips top + bottom. Modeled on the build
// guide's wireframe.
export const CLASSIC_GYM: ZoneTemplate = {
  id: 'classic-gym-board',
  name: 'Classic Gym Board',
  aspectRatio: '16:9',
  zones: [
    { id: 'sponsor-top', type: 'sponsor', x: 1, y: 1, w: 98, h: 9 },
    { id: 'home-stats', type: 'stats', x: 1, y: 12, w: 25, h: 74 },
    { id: 'away-stats', type: 'stats', x: 74, y: 12, w: 25, h: 74 },
    { id: 'home-name', type: 'info', x: 27, y: 12, w: 20, h: 12 },
    { id: 'away-name', type: 'info', x: 53, y: 12, w: 20, h: 12 },
    { id: 'home-score', type: 'score', x: 27, y: 25, w: 14, h: 26 },
    { id: 'game-clock', type: 'clock', x: 42, y: 25, w: 16, h: 26 },
    { id: 'away-score', type: 'score', x: 59, y: 25, w: 14, h: 26 },
    { id: 'home-poss', type: 'info', x: 27, y: 53, w: 14, h: 8 },
    { id: 'period', type: 'clock', x: 42, y: 53, w: 16, h: 12 },
    { id: 'away-poss', type: 'info', x: 59, y: 53, w: 14, h: 8 },
    { id: 'home-fouls', type: 'info', x: 27, y: 67, w: 10, h: 18 },
    { id: 'home-tol', type: 'info', x: 38, y: 67, w: 8, h: 18 },
    { id: 'shot-clock', type: 'clock', x: 46, y: 66, w: 8, h: 20 },
    { id: 'away-tol', type: 'info', x: 54, y: 67, w: 8, h: 18 },
    { id: 'away-fouls', type: 'info', x: 63, y: 67, w: 10, h: 18 },
    { id: 'home-bonus', type: 'info', x: 27, y: 62, w: 14, h: 5 },
    { id: 'away-bonus', type: 'info', x: 59, y: 62, w: 14, h: 5 },
    { id: 'sponsor-bottom', type: 'sponsor', x: 1, y: 88, w: 98, h: 11 },
  ],
  bindings: {
    'home-stats': { kind: 'playerStatsTable', side: 'home' },
    'away-stats': { kind: 'playerStatsTable', side: 'away' },
    'home-name': { kind: 'teamName', side: 'home' },
    'away-name': { kind: 'teamName', side: 'away' },
    'home-score': { kind: 'score', side: 'home' },
    'game-clock': { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' },
    'home-poss': { kind: 'possession', side: 'home' },
    period: { kind: 'period', options: { labelType: 'PERIOD' } },
    'away-poss': { kind: 'possession', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' },
    'home-tol': { kind: 'timeouts', side: 'home' },
    'shot-clock': { kind: 'shotClock' },
    'away-tol': { kind: 'timeouts', side: 'away' },
    'away-fouls': { kind: 'teamFouls', side: 'away' },
    'home-bonus': { kind: 'bonus', side: 'home' },
    'away-bonus': { kind: 'bonus', side: 'away' },
    'sponsor-bottom': {
      kind: 'messageBanner',
      options: { text: 'WE ARE THE WILDCATS' },
    },
  },
};

// Template 5 — Simple (Score + Clock). Big score bar, large logos, a
// center sponsor/media zone, and a foul/TOL strip. No player tables.
export const SIMPLE_BOARD: ZoneTemplate = {
  id: 'simple-score-clock',
  name: 'Simple — Score + Clock',
  aspectRatio: '16:9',
  zones: [
    { id: 'home-name', type: 'info', x: 1, y: 2, w: 32, h: 10 },
    { id: 'period', type: 'clock', x: 34, y: 2, w: 32, h: 10 },
    { id: 'away-name', type: 'info', x: 67, y: 2, w: 32, h: 10 },
    { id: 'home-score', type: 'score', x: 1, y: 14, w: 32, h: 40 },
    { id: 'game-clock', type: 'clock', x: 34, y: 14, w: 32, h: 40 },
    { id: 'away-score', type: 'score', x: 67, y: 14, w: 32, h: 40 },
    { id: 'home-logo', type: 'info', x: 4, y: 56, w: 24, h: 30 },
    { id: 'shot-clock', type: 'clock', x: 34, y: 56, w: 32, h: 30 },
    { id: 'away-logo', type: 'info', x: 72, y: 56, w: 24, h: 30 },
    { id: 'home-fouls', type: 'info', x: 30, y: 88, w: 10, h: 10 },
    { id: 'home-tol', type: 'info', x: 41, y: 88, w: 8, h: 10 },
    { id: 'away-tol', type: 'info', x: 51, y: 88, w: 8, h: 10 },
    { id: 'away-fouls', type: 'info', x: 60, y: 88, w: 10, h: 10 },
  ],
  bindings: {
    'home-name': { kind: 'teamName', side: 'home' },
    period: { kind: 'period' },
    'away-name': { kind: 'teamName', side: 'away' },
    'home-score': { kind: 'score', side: 'home' },
    'game-clock': { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' },
    'home-logo': { kind: 'teamLogo', side: 'home' },
    'shot-clock': { kind: 'shotClock' },
    'away-logo': { kind: 'teamLogo', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' },
    'home-tol': { kind: 'timeouts', side: 'home' },
    'away-tol': { kind: 'timeouts', side: 'away' },
    'away-fouls': { kind: 'teamFouls', side: 'away' },
  },
};

// Template 2 — Video Board, Player Spotlight. Player-stat panels flank a
// center media zone; score bar across the bottom; sponsor strip.
export const VIDEO_SPOTLIGHT: ZoneTemplate = {
  id: 'video-player-spotlight',
  name: 'Video Board — Player Spotlight',
  aspectRatio: '16:9',
  zones: [
    { id: 'home-stats', type: 'stats', x: 1, y: 1, w: 22, h: 66 },
    { id: 'media', type: 'media', x: 24, y: 1, w: 52, h: 66 },
    { id: 'away-stats', type: 'stats', x: 77, y: 1, w: 22, h: 66 },
    { id: 'home-name', type: 'info', x: 1, y: 69, w: 14, h: 18 },
    { id: 'home-score', type: 'score', x: 15, y: 69, w: 14, h: 18 },
    { id: 'game-clock', type: 'clock', x: 30, y: 69, w: 16, h: 18 },
    { id: 'period', type: 'clock', x: 46, y: 69, w: 8, h: 18 },
    { id: 'shot-clock', type: 'clock', x: 54, y: 69, w: 8, h: 18 },
    { id: 'away-score', type: 'score', x: 71, y: 69, w: 14, h: 18 },
    { id: 'away-name', type: 'info', x: 85, y: 69, w: 14, h: 18 },
    { id: 'sponsor', type: 'sponsor', x: 1, y: 89, w: 98, h: 10 },
  ],
  bindings: {
    'home-stats': { kind: 'playerStatsTable', side: 'home' },
    media: { kind: 'mediaZone' },
    'away-stats': { kind: 'playerStatsTable', side: 'away' },
    'home-name': { kind: 'teamName', side: 'home' },
    'home-score': { kind: 'score', side: 'home' },
    'game-clock': { kind: 'gameClock' },
    period: { kind: 'period' },
    'shot-clock': { kind: 'shotClock' },
    'away-score': { kind: 'score', side: 'away' },
    'away-name': { kind: 'teamName', side: 'away' },
    sponsor: { kind: 'sponsorBanner' },
  },
};

// Template 4 — Jumbotron. Full-width media on top, score bar, then team
// stat summaries, message banner along the very bottom.
export const JUMBOTRON: ZoneTemplate = {
  id: 'jumbotron',
  name: 'Jumbotron — Full Video',
  aspectRatio: '16:9',
  zones: [
    { id: 'media', type: 'media', x: 1, y: 1, w: 98, h: 54 },
    { id: 'home-name', type: 'info', x: 1, y: 57, w: 16, h: 14 },
    { id: 'home-score', type: 'score', x: 17, y: 57, w: 14, h: 14 },
    { id: 'game-clock', type: 'clock', x: 33, y: 57, w: 16, h: 14 },
    { id: 'period', type: 'clock', x: 49, y: 57, w: 8, h: 14 },
    { id: 'shot-clock', type: 'clock', x: 57, y: 57, w: 8, h: 14 },
    { id: 'away-score', type: 'score', x: 69, y: 57, w: 14, h: 14 },
    { id: 'away-name', type: 'info', x: 83, y: 57, w: 16, h: 14 },
    { id: 'home-summary', type: 'stats', x: 1, y: 73, w: 48, h: 18 },
    { id: 'away-summary', type: 'stats', x: 51, y: 73, w: 48, h: 18 },
    { id: 'message', type: 'sponsor', x: 1, y: 93, w: 98, h: 6 },
  ],
  bindings: {
    media: { kind: 'mediaZone' },
    'home-name': { kind: 'teamName', side: 'home' },
    'home-score': { kind: 'score', side: 'home' },
    'game-clock': { kind: 'gameClock' },
    period: { kind: 'period' },
    'shot-clock': { kind: 'shotClock' },
    'away-score': { kind: 'score', side: 'away' },
    'away-name': { kind: 'teamName', side: 'away' },
    'home-summary': { kind: 'teamStatsSummary', side: 'home' },
    'away-summary': { kind: 'teamStatsSummary', side: 'away' },
    message: { kind: 'sponsorBanner' },
  },
};

// ===========================================================================
// Arena basketball pack — 3 looks (arena-led / premium-glass / angular)
// × 4 archetypes (full / jumbo / stats / compact). Each look applies its own
// theme object and zone-bg convention:
//   arena-led    → panel/stats/media zones get bg:'#11151c'
//   angular      → score/name/clock zones get bg:'#161b28' so border shows
//   premium-glass→ no zone bg (glass frame supplies the panel)
// ===========================================================================

// --- arena-led -------------------------------------------------------------
export const ARENA_LED_FULL: ZoneTemplate = {
  id: 'arena-led-full',
  name: 'Arena LED — Full Board',
  aspectRatio: '16:9',
  theme: { preset: 'arena-led', font: 'condensed', frame: 'flat', digitStyle: 'segment' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 7 },
    { id: 'winprob', type: 'info', x: 20, y: 9, w: 60, h: 7 },
    { id: 'home-logo', type: 'info', x: 2, y: 18, w: 10, h: 16 },
    { id: 'home-name', type: 'info', x: 2, y: 35, w: 18, h: 7 },
    { id: 'home-score', type: 'score', x: 20, y: 17, w: 16, h: 26 },
    { id: 'poss-home', type: 'info', x: 36, y: 18, w: 4, h: 8 },
    { id: 'clock', type: 'clock', x: 40, y: 17, w: 20, h: 20 },
    { id: 'shot', type: 'clock', x: 46, y: 38, w: 8, h: 8 },
    { id: 'period', type: 'clock', x: 43, y: 38, w: 14, h: 8 },
    { id: 'run', type: 'info', x: 38, y: 47, w: 24, h: 6 },
    { id: 'poss-away', type: 'info', x: 60, y: 18, w: 4, h: 8 },
    { id: 'away-score', type: 'score', x: 64, y: 17, w: 16, h: 26 },
    { id: 'away-logo', type: 'info', x: 88, y: 18, w: 10, h: 16 },
    { id: 'away-name', type: 'info', x: 80, y: 35, w: 18, h: 7 },
    { id: 'home-fouls', type: 'info', x: 20, y: 45, w: 8, h: 8 },
    { id: 'home-tol', type: 'info', x: 29, y: 45, w: 7, h: 8 },
    { id: 'away-tol', type: 'info', x: 64, y: 45, w: 7, h: 8 },
    { id: 'away-fouls', type: 'info', x: 72, y: 45, w: 8, h: 8 },
    { id: 'home-bonus', type: 'info', x: 20, y: 54, w: 16, h: 5 },
    { id: 'away-bonus', type: 'info', x: 64, y: 54, w: 16, h: 5 },
    { id: 'home-stats', type: 'stats', x: 1, y: 61, w: 32, h: 30, bg: '#11151c' },
    { id: 'sponsor', type: 'sponsor', x: 35, y: 80, w: 30, h: 11 },
    { id: 'away-stats', type: 'stats', x: 67, y: 61, w: 32, h: 30, bg: '#11151c' },
  ],
  bindings: {
    arena: { kind: 'venueName' }, winprob: { kind: 'winProbability' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'home-name': { kind: 'teamName', side: 'home' },
    'home-score': { kind: 'score', side: 'home' }, 'poss-home': { kind: 'possession', side: 'home' },
    clock: { kind: 'gameClock' }, shot: { kind: 'shotClock' }, period: { kind: 'period' },
    run: { kind: 'scoringRun' }, 'poss-away': { kind: 'possession', side: 'away' },
    'away-score': { kind: 'score', side: 'away' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    'away-name': { kind: 'teamName', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' }, 'home-tol': { kind: 'timeouts', side: 'home' },
    'away-tol': { kind: 'timeouts', side: 'away' }, 'away-fouls': { kind: 'teamFouls', side: 'away' },
    'home-bonus': { kind: 'bonus', side: 'home' }, 'away-bonus': { kind: 'bonus', side: 'away' },
    'home-stats': { kind: 'playerStatsTable', side: 'home' }, sponsor: { kind: 'sponsorBanner' },
    'away-stats': { kind: 'playerStatsTable', side: 'away' },
  },
};

export const ARENA_LED_JUMBO: ZoneTemplate = {
  id: 'arena-led-jumbo',
  name: 'Arena LED — Jumbotron',
  aspectRatio: '16:9',
  theme: { preset: 'arena-led', font: 'condensed', frame: 'flat', digitStyle: 'segment' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 7 },
    { id: 'media', type: 'media', x: 4, y: 10, w: 92, h: 54, bg: '#11151c' },
    { id: 'home-score', type: 'score', x: 4, y: 66, w: 28, h: 16 },
    { id: 'clock', type: 'clock', x: 36, y: 66, w: 28, h: 16 },
    { id: 'away-score', type: 'score', x: 68, y: 66, w: 28, h: 16 },
    { id: 'home-logo', type: 'info', x: 34, y: 84, w: 8, h: 13 },
    { id: 'away-logo', type: 'info', x: 58, y: 84, w: 8, h: 13 },
    { id: 'run', type: 'info', x: 44, y: 84, w: 12, h: 6 },
    { id: 'home-ts', type: 'stats', x: 4, y: 84, w: 28, h: 13, bg: '#11151c' },
    { id: 'away-ts', type: 'stats', x: 68, y: 84, w: 28, h: 13, bg: '#11151c' },
  ],
  bindings: {
    arena: { kind: 'venueName' }, media: { kind: 'mediaZone' },
    'home-score': { kind: 'score', side: 'home' }, clock: { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    run: { kind: 'scoringRun' },
    'home-ts': { kind: 'teamStatsSummary', side: 'home' }, 'away-ts': { kind: 'teamStatsSummary', side: 'away' },
  },
};

export const ARENA_LED_STATS: ZoneTemplate = {
  id: 'arena-led-stats',
  name: 'Arena LED — Stats Focus',
  aspectRatio: '16:9',
  theme: { preset: 'arena-led', font: 'condensed', frame: 'flat', digitStyle: 'segment' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 6 },
    { id: 'winprob', type: 'info', x: 20, y: 8, w: 60, h: 6 },
    { id: 'home-score', type: 'score', x: 2, y: 15, w: 30, h: 12 },
    { id: 'clock', type: 'clock', x: 37, y: 15, w: 26, h: 12 },
    { id: 'away-score', type: 'score', x: 66, y: 15, w: 30, h: 12 },
    { id: 'period', type: 'clock', x: 43, y: 28, w: 14, h: 6 },
    { id: 'home-stats', type: 'stats', x: 1, y: 36, w: 40, h: 52, bg: '#11151c' },
    { id: 'away-stats', type: 'stats', x: 59, y: 36, w: 40, h: 52, bg: '#11151c' },
    { id: 'home-ts', type: 'stats', x: 42, y: 36, w: 16, h: 26, bg: '#11151c' },
    { id: 'away-ts', type: 'stats', x: 42, y: 63, w: 16, h: 25, bg: '#11151c' },
  ],
  bindings: {
    arena: { kind: 'venueName' }, winprob: { kind: 'winProbability' },
    'home-score': { kind: 'score', side: 'home' }, clock: { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' }, period: { kind: 'period' },
    'home-stats': { kind: 'playerStatsTable', side: 'home' }, 'away-stats': { kind: 'playerStatsTable', side: 'away' },
    'home-ts': { kind: 'teamStatsSummary', side: 'home' }, 'away-ts': { kind: 'teamStatsSummary', side: 'away' },
  },
};

export const ARENA_LED_COMPACT: ZoneTemplate = {
  id: 'arena-led-compact',
  name: 'Arena LED — Compact',
  aspectRatio: '16:9',
  theme: { preset: 'arena-led', font: 'condensed', frame: 'flat', digitStyle: 'segment' },
  zones: [
    { id: 'arena', type: 'info', x: 1, y: 4, w: 18, h: 16 },
    { id: 'home-logo', type: 'info', x: 20, y: 2, w: 8, h: 40 },
    { id: 'home-score', type: 'score', x: 29, y: 2, w: 14, h: 42 },
    { id: 'clock', type: 'clock', x: 44, y: 2, w: 16, h: 42 },
    { id: 'period', type: 'clock', x: 44, y: 48, w: 16, h: 18 },
    { id: 'away-score', type: 'score', x: 57, y: 2, w: 14, h: 42 },
    { id: 'away-logo', type: 'info', x: 72, y: 2, w: 8, h: 40 },
    { id: 'home-fouls', type: 'info', x: 1, y: 52, w: 12, h: 18 },
    { id: 'home-tol', type: 'info', x: 14, y: 52, w: 10, h: 18 },
    { id: 'away-tol', type: 'info', x: 76, y: 52, w: 10, h: 18 },
    { id: 'away-fouls', type: 'info', x: 88, y: 52, w: 11, h: 18 },
    { id: 'winprob', type: 'info', x: 30, y: 70, w: 40, h: 18 },
    { id: 'run', type: 'info', x: 1, y: 72, w: 28, h: 16 },
  ],
  bindings: {
    arena: { kind: 'venueName' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'home-score': { kind: 'score', side: 'home' },
    clock: { kind: 'gameClock' }, period: { kind: 'period' },
    'away-score': { kind: 'score', side: 'away' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' }, 'home-tol': { kind: 'timeouts', side: 'home' },
    'away-tol': { kind: 'timeouts', side: 'away' }, 'away-fouls': { kind: 'teamFouls', side: 'away' },
    winprob: { kind: 'winProbability' }, run: { kind: 'scoringRun' },
  },
};

// --- premium-glass (no zone bg; glass frame supplies the panel) ------------
export const PREMIUM_GLASS_FULL: ZoneTemplate = {
  id: 'premium-glass-full',
  name: 'Premium Glass — Full Board',
  aspectRatio: '16:9',
  theme: { preset: 'premium-glass', font: 'default', frame: 'glass', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 7 },
    { id: 'winprob', type: 'info', x: 20, y: 9, w: 60, h: 7 },
    { id: 'home-logo', type: 'info', x: 2, y: 18, w: 10, h: 16 },
    { id: 'home-name', type: 'info', x: 2, y: 35, w: 18, h: 7 },
    { id: 'home-score', type: 'score', x: 20, y: 17, w: 16, h: 26 },
    { id: 'poss-home', type: 'info', x: 36, y: 18, w: 4, h: 8 },
    { id: 'clock', type: 'clock', x: 40, y: 17, w: 20, h: 20 },
    { id: 'shot', type: 'clock', x: 46, y: 38, w: 8, h: 8 },
    { id: 'period', type: 'clock', x: 43, y: 38, w: 14, h: 8 },
    { id: 'run', type: 'info', x: 38, y: 47, w: 24, h: 6 },
    { id: 'poss-away', type: 'info', x: 60, y: 18, w: 4, h: 8 },
    { id: 'away-score', type: 'score', x: 64, y: 17, w: 16, h: 26 },
    { id: 'away-logo', type: 'info', x: 88, y: 18, w: 10, h: 16 },
    { id: 'away-name', type: 'info', x: 80, y: 35, w: 18, h: 7 },
    { id: 'home-fouls', type: 'info', x: 20, y: 45, w: 8, h: 8 },
    { id: 'home-tol', type: 'info', x: 29, y: 45, w: 7, h: 8 },
    { id: 'away-tol', type: 'info', x: 64, y: 45, w: 7, h: 8 },
    { id: 'away-fouls', type: 'info', x: 72, y: 45, w: 8, h: 8 },
    { id: 'home-bonus', type: 'info', x: 20, y: 54, w: 16, h: 5 },
    { id: 'away-bonus', type: 'info', x: 64, y: 54, w: 16, h: 5 },
    { id: 'home-stats', type: 'stats', x: 1, y: 61, w: 32, h: 30 },
    { id: 'sponsor', type: 'sponsor', x: 35, y: 80, w: 30, h: 11 },
    { id: 'away-stats', type: 'stats', x: 67, y: 61, w: 32, h: 30 },
  ],
  bindings: {
    arena: { kind: 'venueName' }, winprob: { kind: 'winProbability' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'home-name': { kind: 'teamName', side: 'home' },
    'home-score': { kind: 'score', side: 'home' }, 'poss-home': { kind: 'possession', side: 'home' },
    clock: { kind: 'gameClock' }, shot: { kind: 'shotClock' }, period: { kind: 'period' },
    run: { kind: 'scoringRun' }, 'poss-away': { kind: 'possession', side: 'away' },
    'away-score': { kind: 'score', side: 'away' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    'away-name': { kind: 'teamName', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' }, 'home-tol': { kind: 'timeouts', side: 'home' },
    'away-tol': { kind: 'timeouts', side: 'away' }, 'away-fouls': { kind: 'teamFouls', side: 'away' },
    'home-bonus': { kind: 'bonus', side: 'home' }, 'away-bonus': { kind: 'bonus', side: 'away' },
    'home-stats': { kind: 'playerStatsTable', side: 'home' }, sponsor: { kind: 'sponsorBanner' },
    'away-stats': { kind: 'playerStatsTable', side: 'away' },
  },
};

export const PREMIUM_GLASS_JUMBO: ZoneTemplate = {
  id: 'premium-glass-jumbo',
  name: 'Premium Glass — Jumbotron',
  aspectRatio: '16:9',
  theme: { preset: 'premium-glass', font: 'default', frame: 'glass', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 7 },
    { id: 'media', type: 'media', x: 4, y: 10, w: 92, h: 54 },
    { id: 'home-score', type: 'score', x: 4, y: 66, w: 28, h: 16 },
    { id: 'clock', type: 'clock', x: 36, y: 66, w: 28, h: 16 },
    { id: 'away-score', type: 'score', x: 68, y: 66, w: 28, h: 16 },
    { id: 'home-logo', type: 'info', x: 34, y: 84, w: 8, h: 13 },
    { id: 'away-logo', type: 'info', x: 58, y: 84, w: 8, h: 13 },
    { id: 'run', type: 'info', x: 44, y: 84, w: 12, h: 6 },
    { id: 'home-ts', type: 'stats', x: 4, y: 84, w: 28, h: 13 },
    { id: 'away-ts', type: 'stats', x: 68, y: 84, w: 28, h: 13 },
  ],
  bindings: {
    arena: { kind: 'venueName' }, media: { kind: 'mediaZone' },
    'home-score': { kind: 'score', side: 'home' }, clock: { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    run: { kind: 'scoringRun' },
    'home-ts': { kind: 'teamStatsSummary', side: 'home' }, 'away-ts': { kind: 'teamStatsSummary', side: 'away' },
  },
};

export const PREMIUM_GLASS_STATS: ZoneTemplate = {
  id: 'premium-glass-stats',
  name: 'Premium Glass — Stats Focus',
  aspectRatio: '16:9',
  theme: { preset: 'premium-glass', font: 'default', frame: 'glass', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 6 },
    { id: 'winprob', type: 'info', x: 20, y: 8, w: 60, h: 6 },
    { id: 'home-score', type: 'score', x: 2, y: 15, w: 30, h: 12 },
    { id: 'clock', type: 'clock', x: 37, y: 15, w: 26, h: 12 },
    { id: 'away-score', type: 'score', x: 66, y: 15, w: 30, h: 12 },
    { id: 'period', type: 'clock', x: 43, y: 28, w: 14, h: 6 },
    { id: 'home-stats', type: 'stats', x: 1, y: 36, w: 40, h: 52 },
    { id: 'away-stats', type: 'stats', x: 59, y: 36, w: 40, h: 52 },
    { id: 'home-ts', type: 'stats', x: 42, y: 36, w: 16, h: 26 },
    { id: 'away-ts', type: 'stats', x: 42, y: 63, w: 16, h: 25 },
  ],
  bindings: {
    arena: { kind: 'venueName' }, winprob: { kind: 'winProbability' },
    'home-score': { kind: 'score', side: 'home' }, clock: { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' }, period: { kind: 'period' },
    'home-stats': { kind: 'playerStatsTable', side: 'home' }, 'away-stats': { kind: 'playerStatsTable', side: 'away' },
    'home-ts': { kind: 'teamStatsSummary', side: 'home' }, 'away-ts': { kind: 'teamStatsSummary', side: 'away' },
  },
};

export const PREMIUM_GLASS_COMPACT: ZoneTemplate = {
  id: 'premium-glass-compact',
  name: 'Premium Glass — Compact',
  aspectRatio: '16:9',
  theme: { preset: 'premium-glass', font: 'default', frame: 'glass', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 1, y: 4, w: 18, h: 16 },
    { id: 'home-logo', type: 'info', x: 20, y: 2, w: 8, h: 40 },
    { id: 'home-score', type: 'score', x: 29, y: 2, w: 14, h: 42 },
    { id: 'clock', type: 'clock', x: 44, y: 2, w: 16, h: 42 },
    { id: 'period', type: 'clock', x: 44, y: 48, w: 16, h: 18 },
    { id: 'away-score', type: 'score', x: 57, y: 2, w: 14, h: 42 },
    { id: 'away-logo', type: 'info', x: 72, y: 2, w: 8, h: 40 },
    { id: 'home-fouls', type: 'info', x: 1, y: 52, w: 12, h: 18 },
    { id: 'home-tol', type: 'info', x: 14, y: 52, w: 10, h: 18 },
    { id: 'away-tol', type: 'info', x: 76, y: 52, w: 10, h: 18 },
    { id: 'away-fouls', type: 'info', x: 88, y: 52, w: 11, h: 18 },
    { id: 'winprob', type: 'info', x: 30, y: 70, w: 40, h: 18 },
    { id: 'run', type: 'info', x: 1, y: 72, w: 28, h: 16 },
  ],
  bindings: {
    arena: { kind: 'venueName' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'home-score': { kind: 'score', side: 'home' },
    clock: { kind: 'gameClock' }, period: { kind: 'period' },
    'away-score': { kind: 'score', side: 'away' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' }, 'home-tol': { kind: 'timeouts', side: 'home' },
    'away-tol': { kind: 'timeouts', side: 'away' }, 'away-fouls': { kind: 'teamFouls', side: 'away' },
    winprob: { kind: 'winProbability' }, run: { kind: 'scoringRun' },
  },
};

// --- angular (score/name/clock zones get bg:'#161b28' for border) ----------
export const ANGULAR_FULL: ZoneTemplate = {
  id: 'angular-full',
  name: 'Angular — Full Board',
  aspectRatio: '16:9',
  theme: { preset: 'angular', font: 'condensed', frame: 'angular', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 7 },
    { id: 'winprob', type: 'info', x: 20, y: 9, w: 60, h: 7 },
    { id: 'home-logo', type: 'info', x: 2, y: 18, w: 10, h: 16 },
    { id: 'home-name', type: 'info', x: 2, y: 35, w: 18, h: 7, bg: '#161b28' },
    { id: 'home-score', type: 'score', x: 20, y: 17, w: 16, h: 26, bg: '#161b28' },
    { id: 'poss-home', type: 'info', x: 36, y: 18, w: 4, h: 8 },
    { id: 'clock', type: 'clock', x: 40, y: 17, w: 20, h: 20, bg: '#161b28' },
    { id: 'shot', type: 'clock', x: 46, y: 38, w: 8, h: 8, bg: '#161b28' },
    { id: 'period', type: 'clock', x: 43, y: 38, w: 14, h: 8, bg: '#161b28' },
    { id: 'run', type: 'info', x: 38, y: 47, w: 24, h: 6 },
    { id: 'poss-away', type: 'info', x: 60, y: 18, w: 4, h: 8 },
    { id: 'away-score', type: 'score', x: 64, y: 17, w: 16, h: 26, bg: '#161b28' },
    { id: 'away-logo', type: 'info', x: 88, y: 18, w: 10, h: 16 },
    { id: 'away-name', type: 'info', x: 80, y: 35, w: 18, h: 7, bg: '#161b28' },
    { id: 'home-fouls', type: 'info', x: 20, y: 45, w: 8, h: 8 },
    { id: 'home-tol', type: 'info', x: 29, y: 45, w: 7, h: 8 },
    { id: 'away-tol', type: 'info', x: 64, y: 45, w: 7, h: 8 },
    { id: 'away-fouls', type: 'info', x: 72, y: 45, w: 8, h: 8 },
    { id: 'home-bonus', type: 'info', x: 20, y: 54, w: 16, h: 5 },
    { id: 'away-bonus', type: 'info', x: 64, y: 54, w: 16, h: 5 },
    { id: 'home-stats', type: 'stats', x: 1, y: 61, w: 32, h: 30 },
    { id: 'sponsor', type: 'sponsor', x: 35, y: 80, w: 30, h: 11 },
    { id: 'away-stats', type: 'stats', x: 67, y: 61, w: 32, h: 30 },
  ],
  bindings: {
    arena: { kind: 'venueName' }, winprob: { kind: 'winProbability' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'home-name': { kind: 'teamName', side: 'home' },
    'home-score': { kind: 'score', side: 'home' }, 'poss-home': { kind: 'possession', side: 'home' },
    clock: { kind: 'gameClock' }, shot: { kind: 'shotClock' }, period: { kind: 'period' },
    run: { kind: 'scoringRun' }, 'poss-away': { kind: 'possession', side: 'away' },
    'away-score': { kind: 'score', side: 'away' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    'away-name': { kind: 'teamName', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' }, 'home-tol': { kind: 'timeouts', side: 'home' },
    'away-tol': { kind: 'timeouts', side: 'away' }, 'away-fouls': { kind: 'teamFouls', side: 'away' },
    'home-bonus': { kind: 'bonus', side: 'home' }, 'away-bonus': { kind: 'bonus', side: 'away' },
    'home-stats': { kind: 'playerStatsTable', side: 'home' }, sponsor: { kind: 'sponsorBanner' },
    'away-stats': { kind: 'playerStatsTable', side: 'away' },
  },
};

export const ANGULAR_JUMBO: ZoneTemplate = {
  id: 'angular-jumbo',
  name: 'Angular — Jumbotron',
  aspectRatio: '16:9',
  theme: { preset: 'angular', font: 'condensed', frame: 'angular', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 7 },
    { id: 'media', type: 'media', x: 4, y: 10, w: 92, h: 54 },
    { id: 'home-score', type: 'score', x: 4, y: 66, w: 28, h: 16, bg: '#161b28' },
    { id: 'clock', type: 'clock', x: 36, y: 66, w: 28, h: 16, bg: '#161b28' },
    { id: 'away-score', type: 'score', x: 68, y: 66, w: 28, h: 16, bg: '#161b28' },
    { id: 'home-logo', type: 'info', x: 34, y: 84, w: 8, h: 13 },
    { id: 'away-logo', type: 'info', x: 58, y: 84, w: 8, h: 13 },
    { id: 'run', type: 'info', x: 44, y: 84, w: 12, h: 6 },
    { id: 'home-ts', type: 'stats', x: 4, y: 84, w: 28, h: 13 },
    { id: 'away-ts', type: 'stats', x: 68, y: 84, w: 28, h: 13 },
  ],
  bindings: {
    arena: { kind: 'venueName' }, media: { kind: 'mediaZone' },
    'home-score': { kind: 'score', side: 'home' }, clock: { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    run: { kind: 'scoringRun' },
    'home-ts': { kind: 'teamStatsSummary', side: 'home' }, 'away-ts': { kind: 'teamStatsSummary', side: 'away' },
  },
};

export const ANGULAR_STATS: ZoneTemplate = {
  id: 'angular-stats',
  name: 'Angular — Stats Focus',
  aspectRatio: '16:9',
  theme: { preset: 'angular', font: 'condensed', frame: 'angular', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 0, y: 1, w: 100, h: 6 },
    { id: 'winprob', type: 'info', x: 20, y: 8, w: 60, h: 6 },
    { id: 'home-score', type: 'score', x: 2, y: 15, w: 30, h: 12, bg: '#161b28' },
    { id: 'clock', type: 'clock', x: 37, y: 15, w: 26, h: 12, bg: '#161b28' },
    { id: 'away-score', type: 'score', x: 66, y: 15, w: 30, h: 12, bg: '#161b28' },
    { id: 'period', type: 'clock', x: 43, y: 28, w: 14, h: 6, bg: '#161b28' },
    { id: 'home-stats', type: 'stats', x: 1, y: 36, w: 40, h: 52 },
    { id: 'away-stats', type: 'stats', x: 59, y: 36, w: 40, h: 52 },
    { id: 'home-ts', type: 'stats', x: 42, y: 36, w: 16, h: 26 },
    { id: 'away-ts', type: 'stats', x: 42, y: 63, w: 16, h: 25 },
  ],
  bindings: {
    arena: { kind: 'venueName' }, winprob: { kind: 'winProbability' },
    'home-score': { kind: 'score', side: 'home' }, clock: { kind: 'gameClock' },
    'away-score': { kind: 'score', side: 'away' }, period: { kind: 'period' },
    'home-stats': { kind: 'playerStatsTable', side: 'home' }, 'away-stats': { kind: 'playerStatsTable', side: 'away' },
    'home-ts': { kind: 'teamStatsSummary', side: 'home' }, 'away-ts': { kind: 'teamStatsSummary', side: 'away' },
  },
};

export const ANGULAR_COMPACT: ZoneTemplate = {
  id: 'angular-compact',
  name: 'Angular — Compact',
  aspectRatio: '16:9',
  theme: { preset: 'angular', font: 'condensed', frame: 'angular', digitStyle: 'broadcast' },
  zones: [
    { id: 'arena', type: 'info', x: 1, y: 4, w: 18, h: 16 },
    { id: 'home-logo', type: 'info', x: 20, y: 2, w: 8, h: 40 },
    { id: 'home-score', type: 'score', x: 29, y: 2, w: 14, h: 42, bg: '#161b28' },
    { id: 'clock', type: 'clock', x: 44, y: 2, w: 16, h: 42, bg: '#161b28' },
    { id: 'period', type: 'clock', x: 44, y: 48, w: 16, h: 18, bg: '#161b28' },
    { id: 'away-score', type: 'score', x: 57, y: 2, w: 14, h: 42, bg: '#161b28' },
    { id: 'away-logo', type: 'info', x: 72, y: 2, w: 8, h: 40 },
    { id: 'home-fouls', type: 'info', x: 1, y: 52, w: 12, h: 18 },
    { id: 'home-tol', type: 'info', x: 14, y: 52, w: 10, h: 18 },
    { id: 'away-tol', type: 'info', x: 76, y: 52, w: 10, h: 18 },
    { id: 'away-fouls', type: 'info', x: 88, y: 52, w: 11, h: 18 },
    { id: 'winprob', type: 'info', x: 30, y: 70, w: 40, h: 18 },
    { id: 'run', type: 'info', x: 1, y: 72, w: 28, h: 16 },
  ],
  bindings: {
    arena: { kind: 'venueName' },
    'home-logo': { kind: 'teamLogo', side: 'home' }, 'home-score': { kind: 'score', side: 'home' },
    clock: { kind: 'gameClock' }, period: { kind: 'period' },
    'away-score': { kind: 'score', side: 'away' }, 'away-logo': { kind: 'teamLogo', side: 'away' },
    'home-fouls': { kind: 'teamFouls', side: 'home' }, 'home-tol': { kind: 'timeouts', side: 'home' },
    'away-tol': { kind: 'timeouts', side: 'away' }, 'away-fouls': { kind: 'teamFouls', side: 'away' },
    winprob: { kind: 'winProbability' }, run: { kind: 'scoringRun' },
  },
};

export const TEMPLATES: Record<string, ZoneTemplate> = {
  [CLASSIC_GYM.id]: CLASSIC_GYM,
  [SIMPLE_BOARD.id]: SIMPLE_BOARD,
  [VIDEO_SPOTLIGHT.id]: VIDEO_SPOTLIGHT,
  [JUMBOTRON.id]: JUMBOTRON,
  [ARENA_LED_FULL.id]: ARENA_LED_FULL,
  [ARENA_LED_JUMBO.id]: ARENA_LED_JUMBO,
  [ARENA_LED_STATS.id]: ARENA_LED_STATS,
  [ARENA_LED_COMPACT.id]: ARENA_LED_COMPACT,
  [PREMIUM_GLASS_FULL.id]: PREMIUM_GLASS_FULL,
  [PREMIUM_GLASS_JUMBO.id]: PREMIUM_GLASS_JUMBO,
  [PREMIUM_GLASS_STATS.id]: PREMIUM_GLASS_STATS,
  [PREMIUM_GLASS_COMPACT.id]: PREMIUM_GLASS_COMPACT,
  [ANGULAR_FULL.id]: ANGULAR_FULL,
  [ANGULAR_JUMBO.id]: ANGULAR_JUMBO,
  [ANGULAR_STATS.id]: ANGULAR_STATS,
  [ANGULAR_COMPACT.id]: ANGULAR_COMPACT,
};


